import type { KeyboardEvent, MouseEvent, ReactElement } from 'react';

import type { Classnames, Color, IconList, Size } from '@snowit/icetypes';

import { COLOR } from '@snowit/icetypes';
import chroma from '@snowit/utils/chroma';
import { makeCSS } from '@snowit/utils/misc';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import css from './BadgeLabelV2.module.scss';

type TBadgeColor = Color | (string & {});
type TBadgeBackground = TBadgeColor | [TBadgeColor, TBadgeColor];

export interface IBadgeLabelV2 {
  theme?: 'fill' | 'outline';
  shape?: 'default' | 'pill';
  icon?: IconList;
  label: string | number;
  bgColor?: TBadgeBackground;
  labelColor?: TBadgeColor;
  size?: Extract<Size, 'xxl' | 'xl' | 'df'>;
  sliceLabel?: number;
  overflow?: boolean;
  colorID?: string;
  removeLabel?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onRemove?: (event: MouseEvent<HTMLDivElement>) => void;
  classNames?: Classnames<'badge'>;
  id?: string;
  // Jest/Cypress test string
  test?: string;
}

const BadgeLabelV2 = (props: IBadgeLabelV2): ReactElement => {
  const {
    theme = 'fill',
    shape = 'default',
    icon,
    bgColor = 'black',
    labelColor: _labelColor,
    size = 'xl',
    sliceLabel,
    overflow = true,
    colorID,
    removeLabel,
    onClick,
    onRemove,
    classNames,
    id,
    test,
  } = props;

  const [generatedBgColor, generatedLabelColor] = colorID ? chroma.seedColor(colorID) : [];
  const badgeBgColor = generatedBgColor ?? bgColor;

  const resolveBgColor = (color?: TBadgeColor): string | undefined => {
    if (COLOR.includes(color as Color)) return `var(--c-${color})`;
    return color;
  };

  const resolveLabelColor = (color?: TBadgeColor): string | undefined => {
    if (color === undefined) {
      switch (bgColor) {
        case 'warning':
          return 'var(--c-black)';
        default:
          return 'var(--c-white)';
      }
    }

    if (COLOR.includes(color as Color)) return `var(--c-${color})`;
    return color;
  };

  const [colorStart, colorEnd] = Array.isArray(badgeBgColor)
    ? badgeBgColor
    : [badgeBgColor, badgeBgColor];
  const bgColorStart = resolveBgColor(colorStart);
  const bgColorEnd = resolveBgColor(colorEnd);
  const labelColor = generatedLabelColor ?? resolveLabelColor(_labelColor);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (typeof onClick !== 'function') return;
    onClick(event);
  };

  const handleRemove = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (typeof onRemove !== 'function') return;
    onRemove(event);
  };

  const handleRemoveKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.click();
  };

  const renderIcon = () => {
    if (!icon) return null;
    return <Icon className={css.icon} name={icon} color={labelColor} a11y={{ hidden: true }} />;
  };

  const renderLabel = () => {
    const label = sliceLabel === undefined ? props.label : String(props.label).slice(0, sliceLabel);
    return <Typo className={css.label} text={label} tag={'span'} />;
  };

  const renderRemove = () => {
    if (!onRemove) return null;
    return (
      <div
        className={css.removeBtn}
        role={'button'}
        tabIndex={0}
        aria-label={removeLabel ?? String(props.label)}
        onClick={handleRemove}
        onKeyDown={handleRemoveKeyDown}
      >
        <Icon className={css.icon} name={'close'} a11y={{ hidden: true }} />
      </div>
    );
  };

  return (
    <div
      id={id}
      className={cx(css.wrapper, classNames?.badge, css[theme], css[shape], {
        [css.overflow]: overflow,
        [css.onClick]: onClick,
      })}
      style={makeCSS({
        '--l-h': `var(--s-${size})`,
        '--l-color-bg-start': bgColorStart,
        '--l-color-bg-end': bgColorEnd,
        '--l-color-label': labelColor,
      })}
      onClick={handleClick}
      {...testId(test)}
    >
      {renderIcon()}
      {renderLabel()}
      {renderRemove()}
    </div>
  );
};

export default BadgeLabelV2;
